import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_DARK_BG = "https://customer-assets.emergentagent.com/job_agency-build-v1-1/artifacts/3z4djlb0_Mint%20Muse%20logo%202a.png";
const LOGO_LIGHT_BG = "https://customer-assets.emergentagent.com/job_agency-build-v1-1/artifacts/sbn82715_Mint%20Muse%20logo%201a.png";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isLight = scrolled;

  return (
    <header
      data-testid="sticky-header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isLight
          ? "bg-light_base/90 backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[160px] md:h-[200px]">
        {/* Logo */}
        <Link to="/" data-testid="header-logo-link" className="flex-shrink-0">
          <img
            src={isLight ? LOGO_LIGHT_BG : LOGO_DARK_BG}
            alt="Mint Muse Digital"
            className="h-56 md:h-80 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-xs tracking-[0.2em] uppercase font-semibold transition-colors duration-300 ${
                isLight
                  ? "text-dark_base/70 hover:text-dark_base"
                  : "text-cream/70 hover:text-cream"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA + Mobile Toggle */}
        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="header-work-with-us-btn"
            className="hidden md:inline-flex bg-mint text-dark_base font-bold py-2.5 px-6 rounded-full hover:bg-mint-hover transition-colors duration-300 text-sm tracking-wide"
          >
            Work With Us
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isLight ? "text-dark_base" : "text-cream"
            }`}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-dark_base border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="text-cream/80 hover:text-cream text-sm uppercase tracking-[0.15em] font-semibold py-2"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-work-with-us-btn"
                className="bg-mint text-dark_base font-bold py-3 px-6 rounded-full text-center text-sm mt-2"
              >
                Work With Us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
